'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { useEffect, useState } from 'react';

import {
  setNewcarItemsCookies,
} from '@/actions/new-car/set-newcar-items-cookies';
import type { CompareItem } from '@/app/(routes)/listing/types';
import { ComparisonDrawer } from '@/components/comparison-drawer';
import {
  NUM_COMPARISON_MAX_VEHICLES,
} from '@/components/comparison-drawer/contants';
import {
  TITLE_HEADING_H2,
  TITLE_HEADING_H3,
} from '@/components/section-container/base';
import { SectionContainer } from '@/components/section-container/page';
import { TextLink } from '@/components/text-link';
import { useLocalStorage } from '@/hooks/use-localStorage';
import { joinSgcmMsSite, joinSgcmSite } from '@/utils/url/join/public';

import {
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import {
  getMatchStatusSubModels,
  getMinPrice,
} from '../../../_components/page-content/utils';
import {
  NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
  SECTION_TITLE_SIMILAR_NEW_CARS,
  SECTION_TITLE_SIMILAR_USED_CARS,
} from './constants';
import { SimilarNewCarContent } from './similar-new-car-content';
import { SimilarUsedCarItems } from './similar-used-car-items';
import styles from './styles.module.scss';
import { SubModelCompareContent } from './sub-model-compare-content';
import type { Component } from './types';
import { prcMapping, vtsMapping } from './utils';

export const PageContent: Component = ({
  carDetailData,
  similarNewCar,
  similarUsedCar,
  compareList = null,
  modelId,
}) => {
  const hasSimilarNewCarData = similarNewCar.length !== 0;
  const hasSimilarUsedCarData = similarUsedCar.length !== 0;
  const isAdCar = carDetailData.authorizeDealers !== null;

  const { isIpadMini, isDesktop } = useScreenDetect();
  const hasSimilarSameMakeModelUsedCar = similarUsedCar.some(
    usedCar => usedCar.model.includes(`${carDetailData.make} ${carDetailData.model}`),
  );

  const usedCarType = vtsMapping(carDetailData.vehicleType);

  const subModelMinPrice = getMinPrice(carDetailData.subModels);

  const usedCarPrice = prcMapping(subModelMinPrice ?? 0);

  const isIpadMiniMode = isIpadMini();
  const isDesktopMode = isDesktop();

  const [
    compareItems,
    setCompareItems,
  ] = useLocalStorage<CompareItem[]>(
    NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
    compareList ?? [],
  );

  const [
    isComparisonWarningModalOpen,
    setIsComparisonWarningModalOpen,
  ] = useState(false);

  const [
    isComparisonDrawerOpen,
    setIsComparisonDrawerOpen,
  ] = useState(false);

  const [
    isBottomFixedVisible,
    setIsBottomFixedVisible,
  ] = useState(false);

  const handleCompareClearAllClick = async () => {
    const formData = new FormData();

    formData.set('submodelIds', '');
    formData.set('modelIds', '');

    setIsComparisonDrawerOpen(false);
    setCompareItems([]);
    setIsComparisonDrawerOpen(false);
    await setNewcarItemsCookies(formData);
  };

  const handleCompareItemRemove = async (submodelId: number) => {
    const formData = new FormData();
    const newCompareItems = compareItems.filter(
      item => item.submodelId !== submodelId,
    );

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    if (newCompareItems.length === 0) {
      setIsComparisonDrawerOpen(false);
    }
    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const handleDrawerToggleClick = () => {
    setIsComparisonDrawerOpen(prev => !prev);
  };

  const handleComparedCheckboxChange = async (
    compareItem: CompareItem,
    isCompared: boolean,
  ) => {
    const formData = new FormData();

    let newCompareItems: CompareItem[];

    if (isCompared) {
      newCompareItems = [...compareItems, compareItem];
      setIsComparisonDrawerOpen(true);
    } else {
      newCompareItems = compareItems.filter(
        item => item.submodelId !== compareItem.submodelId,
      );

      if (newCompareItems.length === 0) {
        setIsComparisonDrawerOpen(false);
      }
    }

    if (newCompareItems.length > NUM_COMPARISON_MAX_VEHICLES) {
      setIsComparisonWarningModalOpen(true);
      return;
    }

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const matchSubModels = getMatchStatusSubModels(
    carDetailData.additionalProperties.isAvailable,
    carDetailData.subModels,
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsBottomFixedVisible(!entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    const target = document.getElementById('adInfo');
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  return (
    <main
      className={styles.containerPageRoot}>
      <SectionContainer
        className={styles.containerPageContent}
        title=""
        titleHeading={TITLE_HEADING_H2}>
        <SubModelCompareContent
          compareItems={compareItems}
          id={modelId}
          make={carDetailData.make}
          model={carDetailData.model}
          submodels={matchSubModels}
          onComparedCheckboxChange={handleComparedCheckboxChange} />
      </SectionContainer>
      {
        hasSimilarNewCarData && (
          <SectionContainer
            className={styles.containerPageContent}
            title={SECTION_TITLE_SIMILAR_NEW_CARS}
            titleHeading={TITLE_HEADING_H3}>
            <SimilarNewCarContent
              compareItems={compareItems}
              id={modelId}
              make={carDetailData.make}
              model={carDetailData.model}
              similarNewCars={similarNewCar}
              submodels={matchSubModels}
              onComparedCheckboxChange={handleComparedCheckboxChange} />
          </SectionContainer>
        )
      }
      {
        hasSimilarUsedCarData && (
          <SectionContainer
            className={styles.containerPageContent}
            title={SECTION_TITLE_SIMILAR_USED_CARS}
            titleAsideContent={(
              <TextLink
                className={styles.linkViewAll}
                href={hasSimilarSameMakeModelUsedCar
                  ? `${joinSgcmMsSite(`/used-cars/listing?avl=a&regdate-range=<2&q=${carDetailData.make}+${carDetailData.model}`)}`
                  : `${joinSgcmMsSite(`/used-cars/listing?avl=a&${usedCarType}${usedCarPrice}`)}`}
                target="_self"
                text="View All" />
            )}
            titleHeading={TITLE_HEADING_H3}>
            <SimilarUsedCarItems list={similarUsedCar} />
          </SectionContainer>
        )
      }
      <SectionContainer
        className={styles.containerPageContent}
        title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
        titleHeading={TITLE_HEADING_H2}>
        <RecommendedForYouForInfo />
      </SectionContainer>
      <div className={styles.containerComparisonDrawer}>
        <ComparisonDrawer
          comparePageLinkHref={
            isDesktopMode
              ? `${joinSgcmSite('/new_cars/newcars_compare.php')}`
              : `${joinSgcmMsSite(`new-cars/info/${carDetailData.id}/${carDetailData.slug}/compare`)}`
          }
          isOpen={isComparisonDrawerOpen}
          isWarningModalOpen={isComparisonWarningModalOpen}
          items={compareItems}
          setDefaultHeight={!isIpadMiniMode && isAdCar && isBottomFixedVisible}
          onClearAllBtnClick={handleCompareClearAllClick}
          onCloseClick={handleCompareItemRemove}
          onDrawerToggleClick={handleDrawerToggleClick}
          onWarningModalCloseClick={
            () => setIsComparisonWarningModalOpen(false)
          } />
      </div>
    </main>
  );
};
